import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const PMPSchoolsForm = () => {
  const { token, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const [formData, setFormData] = useState({
    customer_id: '',
    service_location_id: '',
    school_name: '',
    campus_name: '',
    date_of_inspection: new Date().toISOString().split('T')[0],
    inspector_name: user?.name || '',
    area_type: 'exterior',
    inspection_items: [],
    additional_notes: '',
    photos: [],
    signature: '',
    forward_to_operations: false
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for education customers
      const educationCustomers = response.data.filter(c => 
        c.name.toLowerCase().includes('school') || 
        c.name.toLowerCase().includes('fau') ||
        c.name.toLowerCase().includes('education') ||
        c.name.toLowerCase().includes('university')
      );
      setCustomers(educationCustomers.length > 0 ? educationCustomers : response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
      setLocations([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setFormData({ ...formData, customer_id: customerId, service_location_id: '', school_name: '', campus_name: '' });
    if (customerId) {
      fetchLocations(customerId);
    } else {
      setLocations([]);
    }
  };

  const handleLocationChange = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      setFormData({
        ...formData,
        service_location_id: locationId,
        school_name: location.location_name,
        campus_name: location.location_name
      });
    }
  };

  // Inspection points per area type
  const inspectionPoints = {
    exterior: [
      'Lights & Fixtures',
      'Security',
      'Fencing & Gates',
      'Painting',
      'Cleanliness / Vandalism',
      'Signage',
      'Playgrounds',
      'Car Stops',
      'Landscape',
      'All Exits',
      'Door Locks / Hardware',
      'Gutters & Building Drains',
      'Asphalt & Parking Lots',
      'Windows',
      'Irrigation Systems',
      'Drop-Off / Pick-Up Areas'
    ],
    classroom: [
      'Lights & Fixtures',
      'Exit/Egress Lighting',
      'Ceiling Tiles',
      'Flooring (Tile, Carpet, VCT)',
      'Walls / Painting',
      'Doors / Locks / Hardware',
      'HVAC Vents / Filters',
      'Windows',
      'Fire Extinguishers',
      'Emergency Lighting',
      'Classroom Fixtures (Desks, Cabinets, Whiteboards)'
    ],
    admin_office: [
      'Lights & Fixtures',
      'Ceiling Tiles',
      'Flooring',
      'Walls / Painting',
      'Doors / Locks / Hardware',
      'HVAC Vents / Filters',
      'Windows',
      'Emergency Lighting'
    ],
    common_area: [
      'Lights & Fixtures',
      'Ceiling Tiles',
      'Flooring',
      'Walls / Painting',
      'Doors / Locks / Hardware',
      'HVAC Vents / Filters',
      'Electrical Rooms',
      'Breaker Panels',
      'Plumbing (General)',
      'Kitchen Appliances',
      'Exhaust Fans',
      'Janitor Closets',
      'Storage Rooms',
      'Windows',
      'Fire Extinguishers',
      'Emergency Lighting',
      'Hallways & Stairwells',
      'Drinking Fountains',
      'Bleachers (Gym)',
      'Scoreboards (Gym)',
      'Athletic Equipment Storage'
    ]
  };

  useEffect(() => {
    // Initialize inspection items when area type changes
    const points = inspectionPoints[formData.area_type] || [];
    setFormData({
      ...formData,
      inspection_items: points.map(item => ({
        item,
        status: 'satisfactory',
        photo: null,
        description: ''
      }))
    });
  }, [formData.area_type]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.inspection_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, inspection_items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/pmp-schools`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('School PM inspection submitted successfully');
      
      // Reset form
      setFormData({
        school_name: '',
        campus_name: '',
        date_of_inspection: new Date().toISOString().split('T')[0],
        inspector_name: user?.name || '',
        area_type: 'exterior',
        inspection_items: [],
        additional_notes: '',
        photos: [],
        signature: '',
        forward_to_operations: false
      });
    } catch (error) {
      toast.error('Failed to submit inspection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card className="mb-6" style={{ backgroundColor: '#D3D3D3' }}>
        <CardHeader>
          <img 
            src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
            alt="TFS Logo" 
            className="h-16 mx-auto mb-4"
          />
          <CardTitle className="text-center text-2xl">
            PREVENTIVE MAINTENANCE – SCHOOLS
          </CardTitle>
          <p className="text-center font-medium">
            MANTENIMIENTO PREVENTIVO – ESCUELAS
          </p>
        </CardHeader>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Header Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Inspection Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Select School / Customer *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={selectedCustomerId}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  required
                >
                  <option value="">-- Select School / Seleccionar Escuela --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Campus / Facility / Campus / Instalación *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.service_location_id}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  disabled={!selectedCustomerId}
                  required
                >
                  <option value="">-- Select Campus / Seleccionar Campus --</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.location_name}
                    </option>
                  ))}
                </select>
                {!selectedCustomerId && (
                  <p className="text-sm text-gray-500 mt-1">Please select a school first</p>
                )}
              </div>

              <div>
                <Label>Date of Inspection / Fecha *</Label>
                <Input
                  type="date"
                  value={formData.date_of_inspection}
                  onChange={(e) => setFormData({ ...formData, date_of_inspection: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Inspector Name / Inspector *</Label>
                <Input
                  value={formData.inspector_name}
                  onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <Label>Area Type / Tipo de Área *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.area_type}
                  onChange={(e) => setFormData({ ...formData, area_type: e.target.value })}
                  required
                >
                  <option value="exterior">Exterior</option>
                  <option value="classroom">Classroom / Salón</option>
                  <option value="admin_office">Admin Office / Oficina</option>
                  <option value="common_area">Common Area / Área Común (Cafeteria, Gym, Hallway)</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inspection Items */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Inspection Points / Puntos de Inspección</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {formData.inspection_items.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{item.item}</h4>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => handleItemChange(index, 'status', 'satisfactory')}
                        className={`px-3 py-1 rounded text-sm ${
                          item.status === 'satisfactory'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        Satisfactory / Satisfactorio
                      </button>
                      <button
                        type="button"
                        onClick={() => handleItemChange(index, 'status', 'needs_attention')}
                        className={`px-3 py-1 rounded text-sm ${
                          item.status === 'needs_attention'
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        Needs Attention / Necesita Atención
                      </button>
                    </div>
                  </div>

                  {item.status === 'needs_attention' && (
                    <div className="mt-3 space-y-2">
                      <Input
                        placeholder="Description of issue / Descripción del problema"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Additional Notes / Notas Adicionales</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-32"
              value={formData.additional_notes}
              onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
              placeholder="General Notes / Notas Generales"
            />
          </CardContent>
        </Card>

        {/* Signature & Forward */}
        <Card className="mb-6">
          <CardContent className="space-y-4 pt-6">
            <div>
              <Label>Inspector Signature / Firma del Inspector *</Label>
              <Input
                value={formData.signature}
                onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                placeholder="Type your full name"
                required
              />
            </div>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.forward_to_operations}
                onChange={(e) => setFormData({ ...formData, forward_to_operations: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">
                Forward to Operations / Enviar a Operaciones
              </span>
            </label>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button type="submit" size="lg" disabled={loading} className="px-12" style={{ backgroundColor: '#6B9F3E' }}>
            {loading ? 'Submitting...' : 'Submit Inspection / Enviar Inspección'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PMPSchoolsForm;
